import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

// Aperina's brand identity is intentionally ornate: burgundy wordmark inside a
// golden botanical frame, coral flowers, muted teal fan-leaves, and a segmented
// citrus slice. This is a bespoke hero that replaces the generic BrandHero for
// the Aperina brand page only. Everything from BrandHighlights downwards is
// still rendered by the shared template so we keep i18n + testids consistent.

const BURGUNDY = "#5B1A2D";
const GOLD = "#B78535";
const CREAM = "#FBF3E7";
const CORAL = "#E85F6E";

export default function AperinaHero({ brand, inquiryHref }) {
  const { t } = useT();
  const navigate = useNavigate();

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: CREAM, color: BURGUNDY }}
    >
      {/* Ambient background emblem — the clean Aperina badge scaled way up,
          low opacity, no pointer events, safely inside the section (never
          bleeds to page borders). Adds visual richness without distraction. */}
      <img
        aria-hidden
        src="/aperina/aperina-badge.png"
        alt=""
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] max-w-none opacity-[0.06]"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-20 pb-24 lg:pb-32">
        <button
          onClick={() => navigate("/#brands")}
          data-testid={BRAND_IDS.backHome}
          className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-100 transition-opacity mb-10 opacity-70"
          style={{ color: BURGUNDY }}
        >
          <ArrowLeft className="w-4 h-4" /> {t("brandPage.backHome")}
        </button>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — Editorial narrative */}
          <div className="lg:col-span-6">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-6"
              style={{ color: GOLD, letterSpacing: "0.36em" }}
            >
              {t(`brandContent.${brand.slug}.category`)}
            </p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-editorial italic leading-[1.02] text-[clamp(2.75rem,6vw,5.75rem)]"
              style={{ color: BURGUNDY }}
            >
              &ldquo;{t(`brandContent.${brand.slug}.tagline`)}&rdquo;
            </motion.h1>

            {/* Gold script sub-title styled to echo the brand guide's script */}
            <p
              className="font-editorial italic mt-4 text-xl md:text-2xl"
              style={{ color: GOLD }}
            >
              {t("brandContent.aperina.heroSub") ||
                "A single-recipe apéritif, ready to pour."}
            </p>

            <p
              className="mt-10 max-w-lg text-base md:text-lg leading-[1.75]"
              style={{ color: `${BURGUNDY}dd` }}
            >
              {brand.story && (t(`brandContent.${brand.slug}.story`) || brand.story)}
            </p>

            <Link
              to={inquiryHref}
              data-testid={BRAND_IDS.ctaInquiry}
              className="mt-12 inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-transform hover:scale-[1.02] shadow-sm"
              style={{ background: BURGUNDY, color: CREAM }}
            >
              {t("brandPage.partnerCta")}
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                style={{ background: GOLD, color: BURGUNDY }}
              >
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          {/* RIGHT — full ornate emblem (badge + botanical background as one artwork) */}
          <div className="lg:col-span-6 relative flex flex-col items-center">
            <motion.img
              initial={{ opacity: 0, y: 30, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              src="/aperina/aperina-badge.png"
              alt="Aperina ornate emblem with botanical background"
              className="w-full max-w-[720px] mx-auto rounded-3xl drop-shadow-[0_25px_60px_rgba(91,26,45,0.18)]"
            />

            {/* Golden thin divider */}
            <div
              className="mt-4 h-px w-24"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}
            />

            {/* Bottle centred beneath the emblem */}
            <motion.img
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              src={brand.frontImage || brand.image}
              alt="Aperina bottle"
              data-testid={BRAND_IDS.deckImage(brand.slug, 0)}
              className="mt-8 max-h-[420px] w-auto object-contain drop-shadow-[0_30px_60px_rgba(91,26,45,0.25)]"
            />

            {/* Under-bottle SKU line */}
            <p
              className="mt-4 text-xs font-bold uppercase"
              style={{ color: BURGUNDY, letterSpacing: "0.3em" }}
            >
              {t("brandContent.aperina.skuVolume") || "1 L"}
              <span className="mx-2" style={{ color: CORAL }}>
                ●
              </span>
              {t("brandContent.aperina.skuFormat") || "PET Bottle"}
              <span className="mx-2" style={{ color: CORAL }}>
                ●
              </span>
              {t("brandContent.aperina.skuRecipe") || "Original Recipe"}
            </p>
          </div>
        </div>
      </div>

      {/* Fine bottom divider — signature gold hairline */}
      <div
        className="h-[3px] w-full"
        style={{ background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)` }}
      />
    </section>
  );
}
